import {useRef, type Dispatch, type SetStateAction} from 'react';

import {isNil} from 'ramda';

import {
  newVehicleApi,
  type ModelYearResponseBody,
  type PricelistVersionRequestBody,
} from '@dms/api/new-vehicle';

import type {ApiError} from 'shared/web';

import type {
  PricedItemChangeGroup,
  PricedItemChangeRequest,
  PublishedPriceChange,
} from '../components/NewPricelistVersionDialog';
import type {PricedItemChange} from '../types/PricedItemChange';
import {
  ITEM_CHANGE_CANCELLED,
  type PricedItemChangeOutcome,
} from '../types/PricedItemChangeOutcome';

/**
 * The createRemove dialogs of usePublishedPriceSave, one at a time: each opens after the one before
 * it settled.
 */
export function useItemChangeDialogs(
  setChange: Dispatch<SetStateAction<PublishedPriceChange | PricedItemChangeRequest | undefined>>
) {
  const [getModelYear] = newVehicleApi.useLazyGetModelYearQuery();
  /** Item changes that wait for the open one; each opens after the one before it settled. */
  const waitingItemChanges = useRef<Array<() => void>>([]);
  const isItemChangeOpen = useRef(false);
  const lastRequestId = useRef(0);

  const openNextItemChange = () => {
    const next = waitingItemChanges.current.shift();
    if (isNil(next)) {
      isItemChangeOpen.current = false;
      return;
    }
    next();
  };

  /**
   * Opens the createRemove dialog of one write or one batch, after the dialogs before it settled.
   * Resolves 'writtenWithVersion' or ITEM_CHANGE_CANCELLED; rejects with the refusal the dialog
   * does not show.
   */
  const confirmItemChange = (options: {
    yearId: string;
    items: PricedItemChange[];
    groups?: PricedItemChangeGroup[];
    submit: (pricelistVersion: PricelistVersionRequestBody) => Promise<unknown>;
    /** A refusal that names one change keeps the dialog open with this message. */
    failureMessage?: (error: ApiError) => string | undefined;
    /** Takes one change out; resolves the rows left, or nothing when no change is left. */
    onRemoveEntry?: (
      key: string
    ) => Promise<{items: PricedItemChange[]; groups: PricedItemChangeGroup[]} | undefined>;
  }) => {
    const {yearId, onRemoveEntry} = options;
    lastRequestId.current += 1;
    const id = lastRequestId.current;
    return new Promise<PricedItemChangeOutcome>((resolve, reject) => {
      let isSettled = false;
      // Settles this request once and closes its dialog only while it is still the one shown.
      const settle = (outcome: () => void) => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        setChange((current) =>
          current?.mode === 'createRemove' && current.id === id ? undefined : current
        );
        outcome();
        openNextItemChange();
      };
      const open = (modelYear: ModelYearResponseBody) =>
        setChange({
          mode: 'createRemove',
          id,
          modelYear,
          items: options.items,
          groups: options.groups,
          submit: options.submit,
          failureMessage: options.failureMessage,
          onRemoveEntry: isNil(onRemoveEntry)
            ? undefined
            : (key) =>
                void onRemoveEntry(key)
                  .then((left) => {
                    if (isNil(left)) {
                      settle(() => resolve(ITEM_CHANGE_CANCELLED));
                      return;
                    }
                    setChange((current) =>
                      current?.mode === 'createRemove' && current.id === id
                        ? {...current, ...left}
                        : current
                    );
                  })
                  .catch((error: unknown) => settle(() => reject(error))),
          onWritten: () => settle(() => resolve('writtenWithVersion')),
          onCancelled: () => settle(() => resolve(ITEM_CHANGE_CANCELLED)),
          onFailed: (error) => settle(() => reject(error)),
        });
      // A write before, queued or sent in sequence, may have created a pricelist version: each
      // dialog reads the year again, so its default label follows the server, not the cache.
      const openWithCurrentYear = () =>
        void getModelYear({modelYearId: yearId}, false)
          .unwrap()
          .then(open)
          .catch((error: unknown) => settle(() => reject(error)));
      if (!isItemChangeOpen.current) {
        isItemChangeOpen.current = true;
        openWithCurrentYear();
        return;
      }
      waitingItemChanges.current.push(openWithCurrentYear);
    });
  };

  return {confirmItemChange};
}
